/**
 * LossesKPIs Component
 *
 * KPI cards for losses summary.
 * @see Sprint 13 - Pertes
 * @see US-13.4 - API KPIs
 * @see US-13.6 - UI Page Pertes
 */

import { AlertTriangle, CalendarDays, TrendingDown } from 'lucide-react';
import { KPICard, Skeleton } from '@/components/ui';
import { useCurrency } from '@/hooks';
import { formatCentimes } from '@/lib/format';
import { useLossesKPIs } from '../hooks/useLosses';

interface LossesKPIsProps {
  /** Currently selected period filter */
  period: 'today' | '7days' | '30days' | 'all';
  /** Whether to show financial values */
  showFinancials?: boolean;
}

export function LossesKPIs({ period, showFinancials = true }: LossesKPIsProps) {
  const currency = useCurrency();
  const { data: kpis, isLoading } = useLossesKPIs();

  if (isLoading) {
    return (
      <div className={`grid grid-cols-2 ${showFinancials ? 'md:grid-cols-4' : 'md:grid-cols-3'} gap-4`}>
        {[1, 2, 3, showFinancials ? 4 : null].filter(Boolean).map((i) => (
          <Skeleton key={i} className="h-28" />
        ))}
      </div>
    );
  }

  if (!kpis) {
    return null;
  }

  // Calculate total for selected period
  let periodValue = 0;
  switch (period) {
    case 'today':
      periodValue = kpis.today.totalValue;
      break;
    case '7days':
      periodValue = kpis.last7Days.totalValue;
      break;
    case '30days':
    default:
      periodValue = kpis.currentMonth.totalValue;
      break;
  }

  return (
    <div className={`grid grid-cols-2 ${showFinancials ? 'md:grid-cols-4' : 'md:grid-cols-3'} gap-4`}>
      <KPICard
        title="Pertes aujourd'hui"
        value={kpis.today.count}
        subtitle={showFinancials ? formatCentimes(kpis.today.totalValue, currency) : undefined}
        icon={<AlertTriangle className="w-5 h-5" />}
        className={kpis.today.count > 0 ? 'border-danger-200' : undefined}
      />
      <KPICard
        title="7 derniers jours"
        value={kpis.last7Days.count}
        subtitle={showFinancials ? formatCentimes(kpis.last7Days.totalValue, currency) : undefined}
        icon={<CalendarDays className="w-5 h-5" />}
      />
      <KPICard
        title="Mois en cours"
        value={kpis.currentMonth.count}
        subtitle={showFinancials ? formatCentimes(kpis.currentMonth.totalValue, currency) : undefined}
        icon={<TrendingDown className="w-5 h-5" />}
      />
      {showFinancials && (
        <div className="rounded-xl border border-danger-200 bg-danger-50 p-6 shadow-sm">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h3 className="text-sm font-medium text-neutral-500 mb-2">
                Valorisation periode
              </h3>
              <div className="text-3xl font-bold text-danger-600">
                {formatCentimes(periodValue, currency)}
              </div>
            </div>
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-danger-100 text-danger-600">
              <TrendingDown className="w-5 h-5" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
